/**
 * Shared gate primitives — backdrop scrim + brand checkbox.
 * Used by both TCGate and NDAGate components.
 */

import { type ReactNode } from 'react'

/* ---------- GateBackdrop ---------- */

interface GateBackdropProps {
  children: ReactNode
  align?: 'center' | 'bottom'
  variant?: 'warm' | 'cool'
}

export function GateBackdrop({ children, align = 'center', variant = 'warm' }: GateBackdropProps) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background:
          variant === 'warm'
            ? 'linear-gradient(180deg, rgba(26,22,18,0.75) 0%, rgba(46,30,18,0.78) 100%)'
            : 'linear-gradient(180deg, rgba(245,241,234,0.04) 0%, rgba(20,16,10,0.82) 100%)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: align === 'bottom' ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: align === 'bottom' ? 0 : 'var(--space-8)',
        zIndex: 50,
      }}
    >
      {children}
    </div>
  )
}

/* ---------- Checkbox (brand-colored, not system blue) ---------- */

interface CheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  id?: string
  dark?: boolean
}

export function GateCheckbox({ checked, onChange, label, id = 'cb', dark = false }: CheckboxProps) {
  return (
    <label
      htmlFor={id}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          position: 'relative',
          width: 20,
          height: 20,
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          style={{
            appearance: 'none',
            WebkitAppearance: 'none',
            position: 'absolute',
            inset: 0,
            width: 20,
            height: 20,
            margin: 0,
            borderRadius: 4,
            border: `1.5px solid ${checked ? (dark ? '#E88D2A' : 'var(--earth-text-primary)') : dark ? '#3D3629' : 'var(--earth-border)'}`,
            background: checked ? (dark ? '#E88D2A' : 'var(--earth-text-primary)') : 'transparent',
            cursor: 'pointer',
            transition: 'all var(--motion-fast)',
          }}
        />
        {checked && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 5,
              left: 4,
              width: 11,
              height: 6,
              borderLeft: `2px solid ${dark ? '#1A1814' : 'var(--earth-bg)'}`,
              borderBottom: `2px solid ${dark ? '#1A1814' : 'var(--earth-bg)'}`,
              transform: 'rotate(-45deg)',
              pointerEvents: 'none',
            }}
          />
        )}
      </span>
      <span
        style={{
          fontSize: 'var(--text-sm)',
          lineHeight: 1.55,
          color: dark ? '#B8AE9C' : 'var(--earth-text-secondary)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        {label}
      </span>
    </label>
  )
}
